import React, { useState } from 'react';
import { Platform } from 'react-native';
import {
  KeyboardAvoidingView,
  Button,
  ButtonText,
  ButtonSpinner,
  Input,
  InputField,
  Text,
  VStack,
  Heading,
  Box,
  Center,
} from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';
import { useAuthForgotPassword } from '../common/api/endpoints/auth/auth';

const ForgotPassword = ({ navigation }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const { t } = useTranslation();

  const forgotPasswordMutation = useAuthForgotPassword();

  const handleSubmit = async () => {
    if (!email) {
      setErrorMessage(t('forgot-password.errors.email-required'));
      return;
    }

    setLoading(true);
    setErrorMessage('');

    try {
      await forgotPasswordMutation.mutateAsync({
        data: { email },
      });
      setSuccess(true);
    } catch (error) {
      // Don't reveal if email exists or not for security
      setSuccess(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <KeyboardAvoidingView
      w="100%"
      h="100%"
      alignItems="center"
      justifyContent="center"
      behavior={Platform.select({ ios: 'height', android: 'height' })}
      $ios-bg="white"
      $android-bg="white"
    >
      <Box alignItems="center" bg="$white" borderRadius="$lg" height={400} $base-w="100%" $md-w={500} p="$8">
        <VStack space="md" w="100%">
          <Heading size="xl" mb="$4">
            {t('forgot-password.title')}
          </Heading>

          {!success ? (
            <>
              <Text mb="$4">{t('forgot-password.description')}</Text>

              <Input isRequired isDisabled={loading}>
                <InputField
                  placeholder={t('forgot-password.email')}
                  type="email"
                  bg="white"
                  testID="emailInput"
                  onChange={(e) => {
                    if (Platform.OS === 'web') {
                      setEmail(e.target.value);
                      setErrorMessage('');
                    }
                  }}
                  onChangeText={(text) => {
                    setEmail(text);
                    setErrorMessage('');
                  }}
                  value={email}
                  autoCapitalize="none"
                  autoFocus
                  textContentType="emailAddress"
                  autoCompleteType="email"
                  keyboardType="email-address"
                />
              </Input>

              {errorMessage && (
                <Text color="$error900" mt="$2">
                  {errorMessage}
                </Text>
              )}

              <Button w="100%" mt="$4" onPress={handleSubmit} testID="button-submit" isDisabled={loading}>
                {loading && <ButtonSpinner mr="$1" />}
                <ButtonText>{t('forgot-password.submit')}</ButtonText>
              </Button>
            </>
          ) : (
            <Center>
              <Text textAlign="center" mb="$4">
                {t('forgot-password.success')}
              </Text>
            </Center>
          )}

          <Button variant="link" onPress={() => navigation.goBack()} testID="back-to-login" alignSelf="center" mt="$4">
            <ButtonText color="$primary600">{t('forgot-password.back-to-login')}</ButtonText>
          </Button>
        </VStack>
      </Box>
    </KeyboardAvoidingView>
  );
};

export default ForgotPassword;
